import { useState, useEffect } from 'react';
import { MessageCircle, MapPin, Clock, Phone, Instagram, Mail, ChevronDown, Star } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent } from './components/ui/card';
import { Dialog, DialogContent } from './components/ui/dialog';
import { ImageWithFallback } from './components/figma/ImageWithFallback';
import logoImage from './assets/c0b0f646ebd7a316f40ae556860f63fd2b58f477.png';
import clinicImage from './assets/fd1fd4ed4b4d28b1aa86bd18fe0d11ebf06be262.png';
import drEllysaImage from './assets/77fb6a34023ec2d7aa73c2d8b420813da82c2734.png';
import clinicExteriorImage from './assets/525479e0d0214d9f486bcdfcb50bb0d39d2bbdd2.png';
import bracesResult1 from './assets/59cddb109938d6b4e4a39b4c860ef505dc25160f.png';
import bracesResult2 from './assets/40f322fbee14d4f6599f2271e68c87a0277d46a9.png';
import bracesResult3 from './assets/5bc28461bcf65a1b4a7f337a6a5a9e8204c57b7c.png';
import bracesResult4 from './assets/b5a36366be3f1d1e6fec6ce6832c99a77e8fd050.png';
import bracesResult5 from './assets/7e018337f6bd28d45129c46917b89d718702467d.png';
import bracesResult6 from './assets/977afa7a23921911f5e74b22d8a5f5f77d87de5a.png';

export default function App() {
  const [showWhatsApp, setShowWhatsApp] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    // Set page title and meta tags
    document.title = 'Braces & Klinik Pergigian Keluarga Shah Alam | UNI Dental Alam Megah';
    
    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'UNI Dental Alam Megah menawarkan rawatan braces, scaling, tampalan, dan penjagaan pergigian keluarga oleh Dr. Ellysa di Shah Alam. Tempah melalui WhatsApp hari ini!');
    }
  }, []);

  const whatsappNumber = '+601151617134';
  const whatsappLink = `https://wasap.my/601151617134/Hai_UniDental`;

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const treatments = [
    { icon: '🦷', name: 'Rawatan Braces', desc: 'Ortodontik untuk senyuman sempurna' },
    { icon: '✨', name: 'Scaling & Polishing', desc: 'Pembersihan gigi profesional' },
    { icon: '🩹', name: 'Tampalan Gigi', desc: 'Pemulihan gigi yang rosak' },
    { icon: '⚕️', name: 'Pembedahan Mulut Kecil', desc: 'Cabutan dan prosedur kecil' },
    { icon: '🦴', name: 'Rawatan Saluran Akar', desc: 'Menyelamatkan gigi anda' },
    { icon: '😁', name: 'Gigi Palsu', desc: 'Penyelesaian gigi hilang' },
    { icon: '🌉', name: 'Bridges', desc: '"Sambung" gigi yang hilang' },
    { icon: '👨‍⚕️', name: 'Rawatan Umum', desc: 'Penjagaan pergigian menyeluruh' },
  ];

  const testimonials = [
    {
      name: 'Amirah Azmi',
      text: 'Dr. Ellysa sangat prihatin dan mesra. Anak saya yang takut dentist kini gembira datang untuk rawatan braces!',
      rating: 5,
    },
    {
      name: 'Ahmad Rizal',
      text: 'Hasil braces saya menakjubkan! Dr. Ellysa sangat berpengalaman dan profesional. Highly recommended!',
      rating: 5,
    },
    {
      name: 'Farah Aisyah',
      text: 'Klinik yang bersih dan selesa. Dr. Ellysa menjelaskan setiap rawatan dengan terperinci. Saya sangat berpuas hati!',
      rating: 5,
    },
    {
      name: 'Mohd Hakim',
      text: 'Perkhidmatan yang excellent! Harga berpatutan dan hasil yang memuaskan. Terima kasih Dr. Ellysa!',
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <img src={logoImage} alt="UNI Dental Alam Megah Logo" className="h-12" />
            <div className="hidden md:flex space-x-6">
              <button onClick={() => scrollToSection('home')} className="text-blue-900 hover:text-orange-500 transition-colors">Utama</button>
              <button onClick={() => scrollToSection('about')} className="text-blue-900 hover:text-orange-500 transition-colors">Tentang</button>
              <button onClick={() => scrollToSection('braces')} className="text-blue-900 hover:text-orange-500 transition-colors">Braces</button>
              <button onClick={() => scrollToSection('services')} className="text-blue-900 hover:text-orange-500 transition-colors">Perkhidmatan</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-blue-900 hover:text-orange-500 transition-colors">Testimoni</button>
              <button onClick={() => scrollToSection('contact')} className="text-blue-900 hover:text-orange-500 transition-colors">Hubungi</button>
            </div>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
              <Button className="bg-green-600 hover:bg-green-700 text-white">
                <MessageCircle className="mr-2 h-4 w-4" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-24 pb-16 px-4 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-blue-900 text-3xl md:text-4xl font-bold leading-tight">
                Klinik Gigi Shah Alam — Braces dan Rawatan Pergigian Terbaik
              </h1>
              <p className="text-lg text-gray-700">
                Dapatkan senyuman impian anda bersama Dr. Ellysa di UNI Dental Alam Megah, klinik pergigian keluarga yang dipercayai di Seksyen 27, Shah Alam
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto">
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Tempah Sekarang
                  </Button>
                </a>
                <Button 
                  size="lg" 
                  variant="outline" 
                  onClick={() => scrollToSection('about')}
                  className="border-blue-900 text-blue-900 hover:bg-blue-50 w-full sm:w-auto"
                >
                  Ketahui Lebih Lanjut
                  <ChevronDown className="ml-2 h-5 w-5" />
                </Button>
              </div>
              <div className="flex items-center gap-4 pt-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-gray-600">4.9 rating dari pesakit yang berpuas hati</span>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-green-400 rounded-3xl transform rotate-3"></div>
              <img 
                src={drEllysaImage} 
                alt="Dr. Ellysa - Doktor Gigi Berpengalaman di UNI Dental Alam Megah" 
                className="relative rounded-3xl shadow-2xl w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 md:order-1">
              <img 
                src={clinicImage} 
                alt="Klinik UNI Dental Alam Megah yang bersih dan selesa" 
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
            <div className="space-y-6 order-1 md:order-2">
              <h2 className="text-blue-900 text-4xl">
                Tentang UNI Dental Alam Megah
              </h2>
              <p className="text-gray-700">
                UNI Dental Alam Megah adalah klinik pergigian keluarga yang mesra dan profesional di Shah Alam, Selangor. Kami komited untuk memberikan penjagaan pergigian berkualiti tinggi dalam persekitaran yang selesa dan menyenangkan.
              </p>
              
              <div className="bg-blue-50 p-6 rounded-xl border-l-4 border-blue-500">
                <h3 className="text-blue-900 mb-3">Kenali Dr. Ellysa</h3>
                <p className="text-gray-700 mb-4">
                  <strong>Dr. Ellysa (BDS, Manipal University)</strong> adalah pemilik dan doktor gigi yang mengendalikan UNI Dental Alam Megah. Dengan lebih <strong>7 tahun pengalaman</strong>, Dr. Ellysa terkenal dalam rawatan braces dan pergigian kanak-kanak.
                </p>
                <p className="text-gray-700">
                  Tidak seperti kebanyakan klinik yang mengupah doktor luar, Dr. Ellysa <strong>merawat pesakit sendiri</strong> dan memastikan penjagaan terbaik. Ramai doktor gigi lain merujuk pesakit mereka kepada Dr. Ellysa untuk rawatan pakar.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4">
                <div className="text-center">
                  <div className="text-green-600 mb-2">7+</div>
                  <p className="text-sm text-gray-600">Tahun Pengalaman</p>
                </div>
                <div className="text-center">
                  <div className="text-green-600 mb-2">1000+</div>
                  <p className="text-sm text-gray-600">Pesakit Berpuas Hati</p>
                </div>
                <div className="text-center">
                  <div className="text-green-600 mb-2">100%</div>
                  <p className="text-sm text-gray-600">Komitmen</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Braces Highlight Section */}
      <section id="braces" className="py-20 px-4 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-white mb-4 text-4xl">Rawatan Braces Kami</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Pakar dalam rawatan ortodontik untuk semua peringkat umur. Dapatkan senyuman sempurna anda dengan bimbingan Dr. Ellysa
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardContent className="p-6 text-center">
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="text-white mb-2">Pengalaman Luas</h3>
                <p className="text-blue-100">Lebih 7 tahun kepakaran dalam rawatan braces</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardContent className="p-6 text-center">
                <div className="text-4xl mb-4">👨‍👩‍👧‍👦</div>
                <h3 className="text-white mb-2">Mesra Keluarga</h3>
                <p className="text-blue-100">Sesuai untuk kanak-kanak dan dewasa</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardContent className="p-6 text-center">
                <div className="text-4xl mb-4">💎</div>
                <h3 className="text-white mb-2">Hasil Terjamin</h3>
                <p className="text-blue-100">Ramai doktor merujuk kepada Dr. Ellysa</p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <h3 className="text-white text-center mb-8 text-2xl">Hasil Rawatan Kami</h3>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div 
                className="cursor-pointer group"
                onClick={() => setSelectedImage(bracesResult1)}
              >
                <div className="aspect-square bg-white/10 rounded-lg p-2 overflow-hidden hover:scale-105 transition-transform">
                  <img 
                    src={bracesResult1}
                    alt="Hasil rawatan braces - Before & After"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
              <div 
                className="cursor-pointer group"
                onClick={() => setSelectedImage(bracesResult2)}
              >
                <div className="aspect-square bg-white/10 rounded-lg p-2 overflow-hidden hover:scale-105 transition-transform">
                  <img 
                    src={bracesResult2}
                    alt="Hasil rawatan braces ortodontik"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
              <div 
                className="cursor-pointer group"
                onClick={() => setSelectedImage(bracesResult3)}
              >
                <div className="aspect-square bg-white/10 rounded-lg p-2 overflow-hidden hover:scale-105 transition-transform">
                  <img 
                    src={bracesResult3}
                    alt="Hasil rawatan gigi profesional"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
              <div 
                className="cursor-pointer group"
                onClick={() => setSelectedImage(bracesResult4)}
              >
                <div className="aspect-square bg-white/10 rounded-lg p-2 overflow-hidden hover:scale-105 transition-transform">
                  <img 
                    src={bracesResult4}
                    alt="Hasil rawatan braces - Before & After"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
              <div 
                className="cursor-pointer group"
                onClick={() => setSelectedImage(bracesResult5)}
              >
                <div className="aspect-square bg-white/10 rounded-lg p-2 overflow-hidden hover:scale-105 transition-transform">
                  <img 
                    src={bracesResult5}
                    alt="Hasil rawatan braces ortodontik"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
              <div 
                className="cursor-pointer group"
                onClick={() => setSelectedImage(bracesResult6)}
              >
                <div className="aspect-square bg-white/10 rounded-lg p-2 overflow-hidden hover:scale-105 transition-transform">
                  <img 
                    src={bracesResult6}
                    alt="Hasil rawatan gigi profesional"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Treatments Section */}
      <section id="services" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-blue-900 mb-4 text-4xl">Perkhidmatan Kami</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Kami menawarkan pelbagai rawatan pergigian untuk memenuhi keperluan keluarga anda
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {treatments.map((treatment, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow border-2 border-blue-100 hover:border-blue-300">
                <CardContent className="p-6 text-center space-y-3">
                  <div className="text-5xl">{treatment.icon}</div>
                  <h3 className="text-blue-900">{treatment.name}</h3>
                  <p className="text-gray-600 text-sm">{treatment.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white">
                <MessageCircle className="mr-2 h-5 w-5" />
                Tanya Mengenai Rawatan
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 bg-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-blue-900 mb-4 text-4xl">Apa Kata Pesakit Kami</h2>
            <p className="text-xl text-gray-600">
              Kepuasan pesakit adalah keutamaan kami
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white hover:shadow-xl transition-shadow">
                <CardContent className="p-6 space-y-4">
                  <div className="flex gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic">"{testimonial.text}"</p>
                  <div className="pt-4 border-t">
                    <p className="text-blue-900">— {testimonial.name}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-blue-900 mb-4 text-4xl">Galeri Kami</h2>
            <p className="text-xl text-gray-600">
              Lihat klinik kami dan hasil rawatan
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <img 
                src={clinicImage}
                alt="Ruang tunggu klinik UNI Dental yang selesa"
                className="rounded-lg w-full h-64 object-cover shadow-lg"
              />
              <p className="text-center text-gray-600 text-sm">Klinik yang bersih dan selesa</p>
            </div>
            <div className="space-y-2">
              <img 
                src={drEllysaImage}
                alt="Dr. Ellysa - Doktor gigi berpengalaman"
                className="rounded-lg w-full h-64 object-cover shadow-lg"
              />
              <p className="text-center text-gray-600 text-sm">Dr. Ellysa bersama pesakit</p>
            </div>
            <div className="space-y-2">
              <ImageWithFallback
                src={clinicExteriorImage}
                alt="Pesakit yang gembira selepas rawatan"
                className="rounded-lg w-full h-64 object-cover shadow-lg"
              />
              <p className="text-center text-gray-600 text-sm">Pesakit yang berpuas hati</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-white mb-4 text-4xl">Hubungi Kami</h2>
            <p className="text-xl text-blue-100">
              Kami sedia membantu anda. Tempah temujanji anda hari ini!
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <Card className="bg-white/10 backdrop-blur-sm border-white/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="text-white mb-2">Alamat</h3>
                      <p className="text-blue-100">
                        38, Jalan Kota Raja M 27/M,<br />
                        Seksyen 27, 40400 Shah Alam,<br />
                        Selangor
                      </p>
                      <div className="flex gap-3 mt-4 mb-4">
                        <a 
                          href="https://share.google/Hm3JMglY5sAROGlwO" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-block"
                        >
                          <Button variant="outline" size="sm" className="bg-white/20 border-white/30 text-white hover:bg-white/30">
                            <MapPin className="mr-2 h-4 w-4" />
                            Google Maps
                          </Button>
                        </a>
                        <a 
                          href="https://waze.com/ul/hw281phku8" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-block"
                        >
                          <Button variant="outline" size="sm" className="bg-white/20 border-white/30 text-white hover:bg-white/30">
                            🚗 Waze
                          </Button>
                        </a>
                      </div>
                      <div>
                        <img 
                          src={clinicExteriorImage}
                          alt="Klinik Pergigian UNI Dental Alam Megah - Bangunan Luar"
                          className="rounded-lg w-[70%] object-cover shadow-lg"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-sm border-white/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Clock className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-white mb-2">Waktu Operasi</h3>
                      <p className="text-blue-100">
                        Setiap Hari: 10:00 AM – 6:30 PM<br />
                        <span className="text-green-300">Tutup: Khamis & Jumaat</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-sm border-white/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Phone className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="text-white mb-2">Telefon & WhatsApp</h3>
                      <p className="text-blue-100 mb-3">+60 11 5161 7134</p>
                      <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                        <Button className="bg-green-600 hover:bg-green-700 text-white w-full">
                          <MessageCircle className="mr-2 h-4 w-4" />
                          WhatsApp Kami
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-sm border-white/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Instagram className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-white mb-2">Media Sosial</h3>
                      <div className="flex gap-3">
                        <a 
                          href="https://www.instagram.com/uni.dental.alammegah" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-block"
                        >
                          <Button variant="outline" size="sm" className="bg-white/20 border-white/30 text-white hover:bg-white/30">
                            <Instagram className="mr-2 h-4 w-4" />
                            Instagram
                          </Button>
                        </a>
                        <a 
                          href="https://www.tiktok.com/@uni.dental.alammegah" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-block"
                        >
                          <Button variant="outline" size="sm" className="bg-white/20 border-white/30 text-white hover:bg-white/30">
                            🎵 TikTok
                          </Button>
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-2 border border-white/20">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3984.0691843863673!2d101.52087827585656!3d3.045906653824586!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc53b0beb9f9cb%3A0x3d5e5e5e5e5e5e5e!2s38%2C%20Jalan%20Kota%20Raja%20M%2027%2FM%2C%20Seksyen%2027%2C%2040400%20Shah%20Alam%2C%20Selangor!5e0!3m2!1sen!2smy!4v1234567890123!5m2!1sen!2smy"
                width="100%"
                height="100%"
                style={{ border: 0, borderRadius: '12px', minHeight: '500px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Lokasi UNI Dental Alam Megah di Shah Alam"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-950 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <img src={logoImage} alt="UNI Dental Logo" className="h-16 mx-auto mb-4" />
          <p className="text-blue-200 mb-4">
            UNI Dental Alam Megah - Klinik Pergigian Keluarga Anda di Shah Alam
          </p>
          <p className="text-blue-300 text-sm">
            © 2025 UNI Dental Alam Megah. Hak Cipta Terpelihara.
          </p>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      {showWhatsApp && (
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-50 group"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-green-500 rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
            <Button 
              size="lg"
              className="relative bg-green-600 hover:bg-green-700 text-white rounded-full h-16 w-16 shadow-2xl flex items-center justify-center"
            >
              <MessageCircle className="h-8 w-8" />
            </Button>
          </div>
        </a>
      )}

      {/* Image Dialog */}
      <Dialog open={selectedImage !== null} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl w-full p-2">
          {selectedImage && (
            <img 
              src={selectedImage}
              alt="Hasil rawatan - Full view"
              className="w-full h-auto rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
